import type { LiveStageMember } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { StageActions, StageState } from "./stage-controller";

const BUTTON =
  "inline-flex min-h-11 shrink-0 items-center rounded-full px-3.5 text-[13px] font-bold disabled:opacity-50";

/**
 * Hands up and guests on the stream, over the picture so it is in view mid-rip.
 * Invite and Remove ask nothing: the buyer's consent is the confirmation step,
 * and taking somebody off has to be instant. There is no unmute; the guest
 * unmutes themselves.
 */
export function StageCard() {
  const state = useSubtree(StageState);
  const observer = useObserver();
  const stage = observer.watch(state.stage);
  const action = observer.watch(state.action);

  if (!stage || (stage.raised.length === 0 && stage.onStage.length === 0)) return null;

  return (
    <section
      aria-label="Buyers on the stream"
      className="flex w-full max-w-xs flex-col gap-1 rounded-2xl border border-white/[0.08] bg-[rgba(20,20,20,0.85)] p-2 text-[13px]"
    >
      {stage.onStage.map((m) => (
        <OnStageRow key={m.userId} member={m} />
      ))}
      {stage.raised.map((m) => (
        <RaisedRow key={m.userId} member={m} />
      ))}
      {action.error && <p className="px-1.5 py-1 text-xs text-[color:var(--color-danger)]">{action.error}</p>}
    </section>
  );
}

function OnStageRow({ member }: { member: LiveStageMember }) {
  const state = useSubtree(StageState);
  const actions = useSubtree(StageActions);
  const observer = useObserver();
  const guests = observer.watch(state.guests);
  const busy = observer.watch(state.busyUserId) === member.userId;
  const room = guests.find((g) => g.identity === `viewer-${member.userId}`);
  const muted = room?.muted ?? false;

  return (
    <div className="flex items-center gap-2 rounded-xl px-1.5 py-1">
      <span
        className={`size-2 shrink-0 rounded-full ${room?.speaking && !muted ? "bg-[color:var(--color-accent)]" : "bg-white/30"}`}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate font-bold text-white">
        {member.name}
        <span className="font-semibold text-white/50">{muted ? " · muted" : room ? " · on air" : " · joining"}</span>
      </span>
      {!muted && room && (
        <button
          type="button"
          className={`${BUTTON} bg-white/10 text-white`}
          disabled={busy}
          onClick={() => void actions.mute(member.userId)}
        >
          Mute
        </button>
      )}
      <button
        type="button"
        className={`${BUTTON} bg-[color:var(--color-danger)]/20 text-[color:var(--color-danger)]`}
        disabled={busy}
        onClick={() => void actions.remove(member.userId)}
      >
        Take off
      </button>
    </div>
  );
}

function RaisedRow({ member }: { member: LiveStageMember }) {
  const state = useSubtree(StageState);
  const actions = useSubtree(StageActions);
  const observer = useObserver();
  const busy = observer.watch(state.busyUserId) === member.userId;

  return (
    <div className="flex items-center gap-2 rounded-xl px-1.5 py-1">
      <span aria-hidden className="shrink-0">
        ✋
      </span>
      <span className="min-w-0 flex-1 truncate font-bold text-white">
        {member.name}
        {member.hasSpot && <span className="font-semibold text-[color:var(--color-accent)]"> · has a spot</span>}
        {member.invited && <span className="font-semibold text-white/50"> · asked</span>}
      </span>
      {member.invited ? (
        <button
          type="button"
          className={`${BUTTON} bg-white/10 text-white`}
          disabled={busy}
          onClick={() => void actions.remove(member.userId)}
        >
          Cancel
        </button>
      ) : (
        <button
          type="button"
          className={`${BUTTON} bg-[color:var(--color-accent)] text-black`}
          disabled={busy}
          onClick={() => void actions.invite(member.userId)}
        >
          Bring on
        </button>
      )}
    </div>
  );
}
